<script lang="ts" setup>
import { ref, watch } from 'vue'
import type { UploadFile } from 'element-plus'
import { UploadOutlinedIcon } from '@xpos/icons-svg'
import ImageCropperDialog from '@/components/ImageCropperDialog.vue'
import { fileToDataUrl } from '@/utils/misc'
import { compressImage, uploadImage } from '@/composables/useImageUpload'

const $props = withDefaults(defineProps<{
  modelValue?: string
  rootFormData?: any
  enableCrop?: boolean
  aspectRatio?: number
  maxSizeMB?: number
  maxWidthOrHeight?: number
}>(), {
  enableCrop: false,
  maxSizeMB: 0.5,
  maxWidthOrHeight: 1280,
})

const $emit = defineEmits(['change', 'update:modelValue'])
const imageUrl = ref('')
const showCropper = ref(false)
const cropImageSrc = ref('')
const cropperRef = ref<InstanceType<typeof ImageCropperDialog>>()

watch(() => $props.modelValue, (newVal) => {
  if (newVal) imageUrl.value = newVal
}, { immediate: true })

// Compresses and uploads image, then emits result
const uploadBlob = async (blob: Blob) => {
  const compressed = await compressImage(blob, { maxSizeMB: $props.maxSizeMB, maxWidthOrHeight: $props.maxWidthOrHeight })
  const res = await uploadImage(compressed)
  imageUrl.value = res.thumbnail_url
  $emit('change', res.file_url)
  $emit('update:modelValue', res.file_url)
}

const handleUpload = async (file: UploadFile) => {
  if (!file.raw) return
  if ($props.enableCrop) {
    cropImageSrc.value = await fileToDataUrl(file.raw)
    showCropper.value = true
    return
  }
  await uploadBlob(file.raw)
}
const handleCropConfirm = async (blob: Blob) => {
  try {
    await uploadBlob(blob)
    cropperRef.value?.close()
  } catch {
    cropperRef.value?.onError()
  }
}
</script>

<template>
  <el-space :size="0">
    <img class="w-[45px] h-[45px]" :src="imageUrl" v-if="imageUrl" />
    <el-upload class="avatar-uploader ml-[5px] mr-0" :show-file-list="false" :on-change="handleUpload" :auto-upload="false" accept="image/*">
      <el-button type="primary" class="shrink-0 w-[50px]"
        ><el-icon size="20"><UploadOutlinedIcon /></el-icon
      ></el-button>
    </el-upload>
    <ImageCropperDialog ref="cropperRef" v-model="showCropper" :image-src="cropImageSrc"
      :aspect-ratio="$props.aspectRatio" @confirm="handleCropConfirm" />
  </el-space>
</template>
